import { normalizedReplicaCount, type EnvelopeResolution, type SizeBounds } from './capacityEnvelope'
import { looksSecret } from './envFile'
import type { GuidedDraft } from './guided'
import { parseImageRef } from './imageRef'
import type { RentalMode } from './rental'
import { SIZE_AXES, nearLimitAxes, type SizeAxis } from './sizing'

/**
 * The configure summary's checklist: what stops this request, what deserves a
 * second look, and what is already known to be fine.
 *
 * It is always on, but it does not make fields go red early — field errors are
 * still shown only after submit. Block items are the same messages as the
 * field errors, so the two can never disagree; warnings are advisory and never
 * stop the submit.
 */
export type ReadinessLevel = 'ok' | 'warn' | 'block'
export type ReadinessItem = { id: string; level: ReadinessLevel; text: string; targetId?: string }

/** The most items the summary shows before collapsing the rest into "and N more". */
export const READINESS_LIMIT = 5

/** The id of the field an error key belongs to, for jump links and focus. */
export function fieldIdForError(key: string): string {
  if (key === 'build') return 'build-dockerfile'
  if (key === 'providerPrice') return 'config-provider-price'
  if (key === 'resources') return 'cpu'
  if (key.startsWith('size-')) return `${key.slice('size-'.length)}-value`
  if (key.startsWith('port-access-')) return `port-allow-input-${key.slice('port-access-'.length)}`
  if (key.startsWith('port-')) return `port-container-${key.slice('port-'.length)}`
  if (key.startsWith('env-')) return `env-key-${key.slice('env-'.length)}`
  if (key.startsWith('envvalue-')) return `env-value-${key.slice('envvalue-'.length)}`
  if (key === 'plaintext') return 'plaintext-ack'
  if (key === 'httpbody') return 'port-http-body'
  if (key === 'volume-mount' || key === 'volume-size') return key
  return key === 'name' ? 'machine-name' : key === 'image' ? 'container-image' : 'price-cap'
}

const AXIS_NAME: Record<SizeAxis, string> = { cpu: 'CPU', ram: 'memory', storage: 'storage' }

function axisList(axes: readonly SizeAxis[]): string {
  return axes.map((axis) => AXIS_NAME[axis]).join(' and ')
}

export type ReadinessInput = {
  draft: GuidedDraft
  /** The page's validation memo, computed regardless of whether it was submitted. */
  errors: Record<string, string>
  rentalMode: RentalMode | undefined
  /** The host's free-capacity bounds for a host-bound request; null when the size is not measured against a host. */
  hostBounds: SizeBounds | null
  sizeUnavailable: boolean
  /** Competitive only: how many observed hosts can run the request, and at what resolution. */
  envelope: { feasible: number; resolution: EnvelopeResolution } | null
  /** Per-block posted price for a host product; null when the host publishes none; undefined when not applicable or not yet known. */
  postedRate: string | null | undefined
}

export function readiness({ draft, errors, rentalMode, hostBounds, sizeUnavailable, envelope, postedRate }: ReadinessInput): ReadinessItem[] {
  const block: ReadinessItem[] = Object.entries(errors).map(([key, text]) => ({ id: `error-${key}`, level: 'block', text, targetId: fieldIdForError(key) }))
  const warn: ReadinessItem[] = []

  const image = parseImageRef(draft.image)
  if (image && !image.pinned) warn.push({ id: 'image-unpinned', level: 'warn', text: 'Image uses latest', targetId: 'container-image' })

  if (rentalMode === 'RENTAL_MODE_DIRECT_HOST_EXCLUSIVE' && postedRate === null) {
    warn.push({ id: 'no-exclusive-price', level: 'warn', text: 'This host has no exclusive price' })
  }

  for (const [index, row] of draft.env.entries()) {
    const key = row.key.trim()
    if (key && looksSecret(key)) warn.push({ id: `env-secret-${index}`, level: 'warn', text: `${key} is readable by the host`, targetId: `env-key-${index}` })
  }

  const measured = hostBounds && !sizeUnavailable ? hostBounds : null
  const replicas = normalizedReplicaCount(draft)
  const overflow = measured && replicas > 1 ? SIZE_AXES.filter((axis) => draft[axis] * replicas > measured[axis].max) : []
  if (overflow.length > 0) {
    warn.push({ id: 'replica-overflow', level: 'warn', text: `${replicas} replicas need more ${axisList(overflow)} than this host has free`, targetId: 'cpu' })
  }
  // Taking everything is the host product, so only custom capacity warns near the limit.
  const nearLimit = measured && rentalMode === 'RENTAL_MODE_DIRECT_CUSTOM' ? nearLimitAxes(draft, measured) : []
  if (nearLimit.length > 0) {
    warn.push({ id: 'near-limit', level: 'warn', text: `Close to this host's free ${axisList(nearLimit)}`, targetId: 'cpu' })
  }

  const ok: ReadinessItem[] = []
  if (block.length === 0) {
    if (measured && overflow.length === 0 && nearLimit.length === 0) ok.push({ id: 'fits', level: 'ok', text: 'Fits on this host' })
    if (rentalMode === 'RENTAL_MODE_COMPETITIVE' && envelope && envelope.feasible > 0) {
      // The same resolution rule as the Size card: provider totals are an upper bound, not a count of hosts that fit.
      const noun = envelope.resolution === 'provider' ? 'provider' : 'host'
      const plural = envelope.feasible === 1 ? noun : `${noun}s`
      ok.push({
        id: 'feasible',
        level: 'ok',
        text: envelope.resolution === 'provider' ? `At most ${envelope.feasible} ${plural} can run this` : `${envelope.feasible} ${plural} can run this`,
      })
    }
    if (draft.ports.length > 0) ok.push({ id: 'ports', level: 'ok', text: `${draft.ports.length} ${draft.ports.length === 1 ? 'port' : 'ports'}, no duplicates` })
  }

  return [...block, ...warn, ...ok]
}

/** The items the summary shows, and how many it folds into "and N more". */
export function capReadiness(items: readonly ReadinessItem[], limit = READINESS_LIMIT): { shown: ReadinessItem[]; more: number } {
  return { shown: items.slice(0, limit), more: Math.max(0, items.length - limit) }
}
